import React, { useEffect, useState } from "react";
import { absMediaUrl } from "../../utils/mediaUrl";
import { formatAmountForCopy } from "../../utils/currency";
import { ApprovalValue, CapsValue, CommissionEditor, MinRequestEditor, PayoutHoldValue, TrustedValue, useTrustActions } from "./Controls";
import { DjDevices } from "./Devices";
import { countryRow, CountryRow } from "./country";
import { DjDevice, DjUser, FeeTier } from "./types";
import "./TalentCard.css";
import { SHOW_TIERS } from "../../lib/tiersPaused";

// Tier Level (the loyalty ladder) shows as a header pill and a row in Money.
// Hidden while Tier Level is paused: the one switch is src/lib/tiersPaused.ts.

const Icon = ({ d }: { d: React.ReactNode }) => (
  <svg className="ac-ic" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const IC = {
  mail: <Icon d={<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>} />,
  ig: <Icon d={<><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></>} />,
  globe: <Icon d={<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3Z" /></>} />,
  cal: <Icon d={<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></>} />,
  phone: <Icon d={<><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M11 18h2" /></>} />,
  tag: <Icon d={<><path d="M3 12V4h8l10 10-8 8Z" /><circle cx="7.5" cy="8.5" r="1.3" /></>} />,
  award: <Icon d={<><circle cx="12" cy="9" r="6" /><path d="m8.5 14-1.5 8 5-3 5 3-1.5-8" /></>} />,
  pct: <Icon d={<><path d="M19 5 5 19" /><circle cx="7" cy="7" r="2.5" /><circle cx="17" cy="17" r="2.5" /></>} />,
  shield: <Icon d={<><path d="M12 3 4 6v6c0 4.5 3.4 8 8 9 4.6-1 8-4.5 8-9V6Z" /><path d="m9 12 2 2 4-4" /></>} />,
  gauge: <Icon d={<><path d="M4 18a8 8 0 1 1 16 0" /><path d="m12 18 4-6" /></>} />,
  clock: <Icon d={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>} />,
  check: <Icon d={<><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>} />,
};

// Flag as an image from public/flags/4x3 (flag-icons, MIT). A code without a
// file shows nothing rather than a broken image.
function Flag({ code }: { code: string | null }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [code]);
  if (!code || failed) return null;
  return <img className="ac-flag" src={`/flags/4x3/${code.toLowerCase()}.svg`} alt="" aria-hidden="true" onError={() => setFailed(true)} />;
}

function Row({ icon, label, children, top, title }: { icon: React.ReactNode; label: string; children: React.ReactNode; top?: boolean; title?: string }) {
  return (
    <div className={`ac-row${top ? " ac-row--top" : ""}`} title={title}>
      <span className="ac-k">
        {icon}
        {label}
      </span>
      <span className="ac-v">{children}</span>
    </div>
  );
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="ac-sec">
    <h4 className="ac-sec-t">{title}</h4>
    {children}
  </section>
);

// "United States · payouts"; MassPay talent: "Indonesia · ID from Indonesia",
// the ID part a red pill when it isn't the payout country.
export function CountryValue({ row }: { row: CountryRow }) {
  return (
    <span className="ac-country">
      <Flag code={row.code} />
      <b>{row.name}</b>
      <span className="ac-dot">·</span>
      {row.idFrom ? (
        row.idFrom.mismatch ? (
          <span className="ac-warn" title="The ID MassPay checked is from a different country than the one they picked for payouts.">
            ID from {row.idFrom.name}
          </span>
        ) : (
          <span className="ac-muted">ID from {row.idFrom.name}</span>
        )
      ) : (
        <span className="ac-muted">payouts</span>
      )}
    </span>
  );
}

export function TalentCard({
  dj,
  setDj,
  feeTier,
  devices,
}: {
  dj: DjUser;
  setDj: React.Dispatch<React.SetStateAction<DjUser | null>>;
  feeTier: FeeTier | null;
  devices: DjDevice[];
}) {
  const actions = useTrustActions(dj, (patch) => setDj((prev) => (prev ? { ...prev, ...patch } : prev)));
  const avatarSrc = absMediaUrl(dj.avatarUrl);
  const country = countryRow(dj);
  const tier = SHOW_TIERS && feeTier?.enabled && feeTier.tier ? feeTier.tier : null;
  const tierLabel = tier ? `${tier.name} · ${tier.feePct}%` : "";
  const cur = (feeTier?.currency || "usd").toUpperCase();
  const tierTitle = feeTier
    ? [
        `Lifetime earned: ${cur} ${(feeTier.lifetimeEarned || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        feeTier.next
          ? `Next: ${feeTier.next.name} (${100 - feeTier.next.keepPct}%) at ${cur} ${feeTier.next.threshold.toLocaleString()}`
          : "Top of the ladder — 5% forever",
      ].join(" · ")
    : undefined;

  return (
    <div className="adm-card ac-card">
      <div className="ac-head">
        {avatarSrc ? (
          <img className="ac-avatar" src={avatarSrc} alt={dj.name} />
        ) : (
          <div className="ac-avatar">{dj.name[0]?.toUpperCase()}</div>
        )}
        <div className="ac-who">
          <div className="ac-name">{dj.name}</div>
          <div className="ac-handle">
            <span className="pill">@{dj.slug}</span>
            {country && (
              <span className="ac-loc">
                <Flag code={country.code} />
                {country.name}
              </span>
            )}
          </div>
        </div>
        <div className="ac-badges">
          {tier && <span className="ac-badge">{tierLabel}</span>}
          <span className={`ac-badge ${dj.trustedAt ? "ac-badge--green" : "ac-badge--grey"}`}>{dj.trustedAt ? "Trusted" : "Not trusted"}</span>
          <span className={`ac-badge ${dj.capsLifted ? "ac-badge--green" : "ac-badge--grey"}`}>{dj.capsLifted ? "Caps lifted" : "Caps default"}</span>
        </div>
      </div>

      <div className="ac-cols">
        <div>
          <Section title="Identity">
            <Row icon={IC.mail} label="Email">
              <span className="ac-ell">{dj.email}</span>
            </Row>
            {dj.instagram && (
              <Row icon={IC.ig} label="Instagram">
                <a href={`https://instagram.com/${dj.instagram}`} target="_blank" rel="noreferrer" className="adm-ig-link ac-ell">
                  @{dj.instagram}
                </a>
              </Row>
            )}
            {country && (
              <Row icon={IC.globe} label="Country">
                <CountryValue row={country} />
              </Row>
            )}
            <Row icon={IC.cal} label="Joined">
              {new Date(dj.createdAt).toLocaleDateString()}
            </Row>
            <Row icon={IC.phone} label="Devices" top>
              <DjDevices devices={devices} />
            </Row>
          </Section>
        </div>
        <div>
          <Section title="Money">
            <Row icon={IC.tag} label="Price">
              {dj.pricePerRequest ? formatAmountForCopy(dj.pricePerRequest, dj.currency || "usd") : "Free"}
            </Row>
            {tier && (
              <Row icon={IC.award} label="Tier" title={tierTitle}>
                <span className="ac-tier">{tierLabel}</span>
                {feeTier?.overrideActive && typeof feeTier.effectiveKeepPct === "number" && (
                  <span className="ac-muted ac-xs">override {100 - feeTier.effectiveKeepPct}% applies</span>
                )}
              </Row>
            )}
            <Row icon={IC.pct} label="Commission">
              <CommissionEditor dj={dj} feeTier={feeTier} onSaved={(updated) => setDj(updated)} />
            </Row>
            <Row icon={IC.pct} label="Min. request">
              <MinRequestEditor dj={dj} onSaved={(updated) => setDj(updated)} />
            </Row>
            <Row
              icon={IC.clock}
              label="Payout hold"
              top={!dj.stripeAccountId && !dj.payoutHoldLiftedAt}
              title="New accounts hold earnings for 7 days before they can be paid out (Stripe delay_days). Lifting drops it to Stripe's minimum for the country — 2 days in the US."
            >
              <PayoutHoldValue dj={dj} a={actions} />
            </Row>
          </Section>
          <Section title="Controls">
            <Row icon={IC.check} label="Approval" title="Approve turns on paid requests. Only needed here for someone who isn't on the Approvals list.">
              <ApprovalValue dj={dj} />
            </Row>
            <Row
              icon={IC.shield}
              label="Trusted"
              title="One tap for a DJ you know personally: lifts caps, lifts the payout hold, and skips the first-cashout check. Removing trust restores the default caps."
            >
              <TrustedValue dj={dj} a={actions} />
            </Row>
            <Row
              icon={IC.gauge}
              label="Caps"
              title="Default = the tight fraud-door caps ($200 request / $200 boost / $400 counter class, per currency). Lifted = $1,000 request / $2,000 boost / $10,000 counter class. Lift only for a DJ you know is real."
            >
              <CapsValue dj={dj} a={actions} />
            </Row>
          </Section>
        </div>
      </div>
    </div>
  );
}
